import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';

// Admin Components
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminHeader from '../components/admin/AdminHeader';
import RevenueIntelligence from '../components/admin/RevenueIntelligence';
import OperationalModule from '../components/admin/OperationalModule';
import SystemHealthPanel from '../components/admin/SystemHealthPanel';
import LiveEventsStream from '../components/admin/LiveEventsStream';
import SubscriptionAnalytics from '../components/admin/SubscriptionAnalytics';
import CryptoInvoicesPanel from '../components/admin/CryptoInvoicesPanel';
import BillingReconciliation from '../components/admin/BillingReconciliation';
import BillingAnomaliesPanel from '../components/admin/BillingAnomaliesPanel';
import UserBillingTimeline from '../components/admin/UserBillingTimeline';
import UserManagementPanel from '../components/UserManagementPanel';
import StripeProductManager from '../components/StripeProductManager';
import ContractVault from '../components/ContractVault';
import BugReportsPanel from '../components/BugReportsPanel';
import KYCSubmissionsPanel from '../components/admin/KYCSubmissionsPanel';
import AdminTasksPanel from '../components/admin/AdminTasksPanel';
import QuickAccessCredentials from '../components/admin/QuickAccessCredentials';
import TestimonialsPanel from '../components/admin/TestimonialsPanel';
import EmailTemplatesPanel from '../components/admin/EmailTemplatesPanel';
import ReengagementCampaignPanel from '../components/admin/ReengagementCampaignPanel';
import BroadcastEmailPanel from '../components/admin/BroadcastEmailPanel';
import FoundingMembersPanel from '../components/admin/FoundingMembersPanel';
import GrowthFeedbackPanel from '../components/admin/GrowthFeedbackPanel';
import GrowthMetricsPanel from '../components/admin/GrowthMetricsPanel';
import AffiliatePayoutsPanel from '../components/admin/AffiliatePayoutsPanel';
import SocialPlaybookPanel from '../components/admin/SocialPlaybookPanel';
import CompanyInvoicesPanel from '../components/admin/CompanyInvoicesPanel';
import PaymentSettingsPanel from '../components/admin/PaymentSettingsPanel';
import AISettingsPanel from '../components/admin/AISettingsPanel';
import LandingDesignPanel from '../components/admin/LandingDesignPanel';
import TwitterGrowthPanel from '../components/admin/TwitterGrowthPanel';

import * as FiIcons from 'react-icons/fi';

const { 
  FiUsers, FiRefreshCw, FiAlertTriangle, FiShield, FiDollarSign, 
  FiLoader, FiActivity, FiArchive, FiCpu, FiBarChart2,
  FiPackage, FiBug, FiDatabase, FiCreditCard, FiSearch, FiUserCheck, FiGift
} = FiIcons;

const AdminDashboard = () => {
  const { user } = useAuth();
  const { isAdmin, profile, loading: authLoading } = useSubscription();
  
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  // Mobile drawer state — desktop ignores this (sidebar is always
  // visible on md+). Reset to closed on every section change so the
  // drawer never sticks open behind the new content.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  // Module open states
  const [openModules, setOpenModules] = useState({
    liveEvents: true,
    subscriptions: false,
    crypto: false,
    reconciliation: false,
    anomalies: false,
    users: false,
    products: false,
    contracts: false,
    bugs: false,
    kyc: false,
  });

  // User timeline modal
  const [selectedUserForTimeline, setSelectedUserForTimeline] = useState(null);

  // Stats for badges
  const [stats, setStats] = useState({
    pendingInvoices: 0,
    anomalies: 0,
    openBugs: 0,
    pendingKyc: 0,
  });

  const fetchStats = async () => {
    try {
      // Fetch pending invoices count
      const { data: invoices } = await supabase
        .from('crypto_invoices')
        .select('id')
        .eq('status', 'pending');
      
      // Fetch anomalies count (24h)
      const { data: anomalies } = await supabase
        .from('billing_events')
        .select('id')
        .eq('is_anomaly', true)
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
      
      // Fetch open bugs count
      const { data: bugs } = await supabase
        .from('bug_reports')
        .select('id')
        .eq('status', 'open');

      // Fetch pending KYC count
      const { data: kyc } = await supabase
        .from('kyc_submissions')
        .select('id')
        .eq('status', 'pending');

      setStats({
        pendingInvoices: invoices?.length || 0,
        anomalies: anomalies?.length || 0,
        openBugs: bugs?.length || 0,
        pendingKyc: kyc?.length || 0,
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  const toggleModule = (module) => {
    setOpenModules(prev => ({ ...prev, [module]: !prev[module] }));
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchStats();
    setTimeout(() => setLoading(false), 1000);
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-900 text-white">
        <FiLoader className="animate-spin text-purple-500 text-5xl mb-4" />
        <p className="text-slate-400">Loading admin dashboard...</p>
      </div>
    );
  }

  // Admin verification
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-900 text-white p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 max-w-md text-center">
          <FiAlertTriangle className="text-red-400 text-5xl mx-auto mb-4" />
          <h1 className="text-xl font-bold text-red-300 mb-2">Admin Access Required</h1>
          <p className="text-slate-400 text-sm mb-4">
            You don't have permission to access this area.
          </p>
          <p className="text-xs text-slate-500 mb-6">
            Connected as: {user?.email}<br />
            Role: {profile?.role || 'user'}
          </p>
          <a
            href="/dashboard"
            className="inline-block bg-gradient-to-r from-purple-600 to-cyan-600 text-white py-2 px-6 rounded-xl font-medium"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Render section based on sidebar selection
  const renderMainContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            {/* Revenue Intelligence */}
            <RevenueIntelligence />
          </div>
        );

      case 'live-events':
        return <LiveEventsStream />;

      case 'tasks':
        return <AdminTasksPanel />;

      case 'users':
        return <UserManagementPanel />;

      case 'subscriptions':
        return <SubscriptionAnalytics />;

      case 'billing':
        return (
          <div className="space-y-6">
            <BillingReconciliation />
          </div>
        );

      case 'emails':
        return <EmailTemplatesPanel />;

      case 'reengagement':
        return <ReengagementCampaignPanel />;
      case 'broadcast':
        return <BroadcastEmailPanel />;
      case 'founding':
        return <FoundingMembersPanel />;
      case 'feedback':
        return <GrowthFeedbackPanel />;

      case 'growth':
        return <GrowthMetricsPanel />;

      case 'twitter-growth':
        return <TwitterGrowthPanel />;

      case 'affiliate-payouts':
        return <AffiliatePayoutsPanel />;

      case 'social-playbook':
        return <SocialPlaybookPanel />;

      case 'company-invoices':
        return <CompanyInvoicesPanel />;

      case 'crypto':
        return <CryptoInvoicesPanel />;

      case 'anomalies':
        return <BillingAnomaliesPanel onViewUser={setSelectedUserForTimeline} />;

      case 'products':
        return <StripeProductManager />;

      case 'contracts':
        return <ContractVault />;

      case 'bugs':
        return <BugReportsPanel />;

      case 'kyc':
        return <KYCSubmissionsPanel />;

      case 'testimonials':
        return <TestimonialsPanel />;

      case 'health':
        return <SystemHealthPanel />;

      case 'payment-settings':
        return <PaymentSettingsPanel />;

      case 'ai-settings':
        return <AISettingsPanel />;

      case 'landing-design':
        return <LandingDesignPanel />;

      case 'quick-access':
        return <QuickAccessCredentials />;

      case 'settings':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Settings</h2>
            <p className="text-slate-400">Admin settings and configuration.</p>
          </div>
        );

      default:
        return (
          <div className="space-y-8">
            {/* Revenue Intelligence */}
            <RevenueIntelligence />

            {/* Operational Modules Grid */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
                <FiDatabase className="mr-2 text-purple-400" />
                Operational Modules
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <OperationalModule
                  title="Live Events"
                  subtitle="Real-time billing activity"
                  icon={FiActivity}
                  status="active"
                  statusLabel="Live"
                  onClick={() => setActiveSection('live-events')}
                  testId="module-live-events"
                />
                <OperationalModule
                  title="User Management"
                  subtitle="Manage roles and subscriptions"
                  icon={FiUsers}
                  status="active"
                  statusLabel="Active"
                  onClick={() => setActiveSection('users')}
                  testId="module-users"
                />
                <OperationalModule
                  title="Subscription Analytics"
                  subtitle="Plan distribution and usage"
                  icon={FiBarChart2}
                  status="active"
                  statusLabel="Active"
                  onClick={() => setActiveSection('subscriptions')}
                  testId="module-subscriptions"
                />
                <OperationalModule
                  title="Crypto Invoices"
                  subtitle="Manage cryptocurrency payments"
                  icon={FiDatabase}
                  status={stats.pendingInvoices > 0 ? 'warning' : 'active'}
                  statusLabel={stats.pendingInvoices > 0 ? `${stats.pendingInvoices} Pending` : 'Active'}
                  badge={stats.pendingInvoices > 0 ? stats.pendingInvoices : undefined}
                  onClick={() => setActiveSection('crypto')}
                  testId="module-crypto"
                />
                <OperationalModule
                  title="Billing Reconciliation"
                  subtitle="Revenue and billing health"
                  icon={FiDollarSign}
                  status="active"
                  statusLabel="Active"
                  onClick={() => setActiveSection('billing')}
                  testId="module-billing"
                />
                <OperationalModule
                  title="Billing Anomalies"
                  subtitle="Suspicious activities and fraud"
                  icon={FiAlertTriangle}
                  status={stats.anomalies > 0 ? 'warning' : 'active'}
                  statusLabel={stats.anomalies > 0 ? `${stats.anomalies} Flagged` : 'Clear'}
                  badge={stats.anomalies > 0 ? stats.anomalies : undefined}
                  onClick={() => setActiveSection('anomalies')}
                  testId="module-anomalies"
                />
                <OperationalModule
                  title="Stripe Products"
                  subtitle="Manage pricing and products"
                  icon={FiPackage}
                  status="active"
                  statusLabel="Configured"
                  onClick={() => setActiveSection('products')}
                  testId="module-products"
                />
                <OperationalModule
                  title="Contract Vault"
                  subtitle="Legal documents and contracts"
                  icon={FiArchive}
                  status="active"
                  statusLabel="Secure"
                  onClick={() => setActiveSection('contracts')}
                  testId="module-contracts"
                />
                <OperationalModule
                  title="Bug Reports"
                  subtitle="User-reported issues"
                  icon={FiBug}
                  status={stats.openBugs > 0 ? 'warning' : 'active'}
                  statusLabel={stats.openBugs > 0 ? `${stats.openBugs} Open` : 'Clear'}
                  badge={stats.openBugs > 0 ? stats.openBugs : undefined}
                  onClick={() => setActiveSection('bugs')}
                  testId="module-bugs"
                />
                <OperationalModule
                  title="KYC Submissions"
                  subtitle="User identity verification"
                  icon={FiUserCheck}
                  status={stats.pendingKyc > 0 ? 'warning' : 'active'}
                  statusLabel={stats.pendingKyc > 0 ? `${stats.pendingKyc} Pending` : 'Clear'}
                  badge={stats.pendingKyc > 0 ? stats.pendingKyc : undefined}
                  onClick={() => setActiveSection('kyc')}
                  testId="module-kyc"
                />
              </div>
            </div>

            {/* System Health */}
            <SystemHealthPanel />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar — desktop-fixed on md+, drawer on mobile */}
      <AdminSidebar
        activeSection={activeSection}
        onSectionChange={(s) => { setActiveSection(s); setMobileNavOpen(false); }}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Main Content Area — only offset by the sidebar on md+. On
          mobile the sidebar is a drawer, so content spans full width. */}
      <div className="flex-1 md:ml-64 min-w-0">
        {/* Mobile top bar — hamburger + admin label. Hidden on md+
            since the sidebar handles branding there. */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="p-2 rounded-lg border border-slate-700/60 text-slate-300 hover:bg-slate-800/70"
            aria-label="Open admin menu"
          >
            <FiIcons.FiMenu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
              <FiIcons.FiShield className="text-white text-sm" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-white truncate">Admin</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 truncate">
                {activeSection.replace(/-/g, ' ')}
              </div>
            </div>
          </div>
        </div>

        {/* Header — hidden on mobile; the sticky top bar above replaces it. */}
        <div className="hidden md:block">
          <AdminHeader
            userEmail={user?.email || ''}
            userRole={profile?.role || 'admin'}
            onRefresh={handleRefresh}
            loading={loading}
          />
        </div>

        {/* Main Content — tighter padding on mobile so panels aren't
            crushed against the edge. */}
        <main className="p-4 md:p-6">
          {/* keep the existing markup right after this line */}

          {/* Search Bar (for users/invoices) */}
          {['users', 'crypto', 'anomalies'].includes(activeSection) && (
            <div className="mb-6">
              <div className="relative max-w-md">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search users, invoices, events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                />
              </div>
            </div>
          )}

          {/* Back button for detail views */}
          {activeSection !== 'overview' && (
            <motion.button
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => setActiveSection('overview')}
              className="mb-6 flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
            >
              <span>←</span>
              <span className="text-sm">Back to Overview</span>
            </motion.button>
          )}

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderMainContent()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* User Billing Timeline Modal */}
      <AnimatePresence>
        {selectedUserForTimeline && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedUserForTimeline(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700/50"
              onClick={(e) => e.stopPropagation()}
            >
              <UserBillingTimeline 
                userId={selectedUserForTimeline} 
                onBack={() => setSelectedUserForTimeline(null)} 
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;